import { useState } from "react";
import { toast } from "sonner";
import { Mail, MessageCircle, Globe, Clock, Send, MapPin } from "lucide-react";
import LegalLayout from "@/components/LegalLayout";
import { Button, Input } from "@/components/kit";
import api, { apiErr } from "@/lib/api";
import { COMPANY } from "@/lib/legal";

const CHANNELS = [
  {
    icon: Mail, color: "sky", label: "Email",
    value: COMPANY.email, href: `mailto:${COMPANY.email}`,
    desc: "Respon dalam 1×24 jam pada hari kerja.",
  },
  {
    icon: MessageCircle, color: "emerald", label: "WhatsApp",
    value: COMPANY.whatsappDisplay, href: `https://wa.me/${COMPANY.whatsapp}`,
    desc: "Chat langsung untuk bantuan cepat.",
  },
  {
    icon: Globe, color: "violet", label: "Website",
    value: "resada.id", href: COMPANY.website,
    desc: "Pelajari fitur & paket terbaru.",
  },
];

const COLORS = {
  sky: "bg-sky-50 text-sky-600 border-sky-100",
  emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
  violet: "bg-violet-50 text-violet-600 border-violet-100",
};

export default function Kontak() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) { toast.error("Nama minimal 2 karakter"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { toast.error("Email tidak valid"); return; }
    if (form.message.trim().length < 5) { toast.error("Pesan minimal 5 karakter"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/public/contact", form);
      toast.success(data.message || "Pesan terkirim! Tim kami akan menghubungi Anda.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSending(false); }
  };

  return (
    <LegalLayout
      seoTitle={`Kontak — ${COMPANY.brand}`}
      seoDesc={`Hubungi tim ${COMPANY.brand}. Email ${COMPANY.email}, WhatsApp ${COMPANY.whatsappDisplay}, atau kirim pesan lewat formulir kontak.`}
      path="/kontak"
      title="Hubungi Kami"
      subtitle={`Punya pertanyaan tentang ${COMPANY.brand}, pembayaran, atau butuh bantuan teknis? Tim kami siap membantu.`}
    >
      <div className="grid lg:grid-cols-2 gap-10">
        {/* Channels */}
        <div className="space-y-4" data-testid="contact-channels">
          {CHANNELS.map((c) => {
            const Icon = c.icon;
            return (
              <a
                key={c.label}
                href={c.href}
                target={c.href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                data-testid={`contact-channel-${c.label.toLowerCase()}`}
                className="flex items-start gap-4 rounded-2xl border border-slate-200 p-5 hover:border-sky-300 hover:shadow-sm transition bg-white"
              >
                <span className={`w-11 h-11 rounded-xl grid place-items-center border ${COLORS[c.color]}`}>
                  <Icon className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{c.label}</p>
                  <p className="font-display font-bold text-slate-900">{c.value}</p>
                  <p className="text-sm text-slate-500 mt-0.5">{c.desc}</p>
                </div>
              </a>
            );
          })}
          <div className="rounded-2xl bg-slate-50 border border-slate-100 p-5 space-y-3">
            <div className="flex items-center gap-3 text-sm text-slate-600">
              <Clock className="w-4 h-4 text-slate-400 shrink-0" />
              <span>Jam operasional: Senin–Jumat, 09.00–18.00 WIB</span>
            </div>
            <a href={`https://maps.google.com/?q=${encodeURIComponent(COMPANY.address)}`} target="_blank" rel="noreferrer"
              data-testid="kontak-address-link"
              className="flex items-start gap-3 text-sm text-slate-600 hover:text-sky-600 transition">
              <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
              <span className="leading-relaxed">{COMPANY.address}</span>
            </a>
          </div>
        </div>

        {/* Form */}
        <div className="rounded-2xl border border-slate-200 p-6 bg-white">
          <h2 className="font-display text-xl font-bold text-slate-900">Kirim Pesan</h2>
          <p className="text-sm text-slate-500 mt-1">Isi formulir di bawah, kami akan membalas ke email Anda.</p>
          <form onSubmit={submit} className="mt-5 space-y-4" data-testid="kontak-form">
            <div>
              <label className="text-xs font-semibold text-slate-500">Nama</label>
              <Input data-testid="kontak-name" placeholder="Nama lengkap" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Email</label>
              <Input data-testid="kontak-email" type="email" placeholder="email@domain.com" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Pesan</label>
              <textarea data-testid="kontak-message" rows={5} placeholder="Tulis pertanyaan atau pesan Anda..."
                value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full mt-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-400 resize-none" />
            </div>
            <Button type="submit" data-testid="kontak-submit" loading={sending} className="w-full">
              <Send className="w-4 h-4" /> Kirim Pesan
            </Button>
          </form>
        </div>
      </div>
    </LegalLayout>
  );
}
